import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./DocumentsDrawings.module.scss";

const DocumentsDrawings: FC = () => {
  return (
    <div className={styles.documentsPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Documents & Drawings"
        heading="All your building documents and drawings. One place."
        copy="Commercial buildings can contain thousands of technical documents and drawings. Pulse provides a structured way to organise, access and find the information your teams need."
        backgroundImage={require("../../assets/images/documents-drawings/documents-drawings-hero.webp")}
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              Buildings generate thousands of documents. Finding the right one
              shouldn't be a job in itself.
            </h2>
            <p className={styles.sectionCopy}>
              A commercial building can accumulate a huge amount of technical
              information throughout its design, construction, fit-out and
              operational life.
            </p>
            <p className={styles.sectionCopy}>
              Without a clear structure, finding the right document can mean
              navigating folders, opening PDFs or relying on someone who knows
              where the information is stored.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/documents-drawings-fragmented.webp")}
              alt="Fragmented building documents and drawings"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. More Than File Storage */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/information-architecture.webp")}
              alt="Pulse information structure navigation"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Information Architecture</span>
            <h2 className={styles.sectionHeading}>
              A building information structure, not just a folder hierarchy.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse organises documents and drawings within the wider structure
              of the building. Instead of treating every file as an isolated
              item, documents are organised according to the information they
              represent.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. Documents */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Technical Documents</span>
          <h2 className={styles.sectionHeading}>
            Keep technical documents organised and accessible.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse provides a structured home for the documents that support the
            operation and management of a building.
          </p>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>O&M Manuals</h3>
            <p>Comprehensive operation and maintenance records.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Equipment Manuals</h3>
            <p>Manufacturer guidance and specifications for plant.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Specifications</h3>
            <p>Design and technical building specifications.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Technical Reports</h3>
            <p>Surveys, inspections and engineering reports.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Certificates</h3>
            <p>Compliance, safety and statutory documentation.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Operating Procedures</h3>
            <p>Step-by-step building and plant operating guides.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Maintenance Info</h3>
            <p>Servicing schedules and maintenance instructions.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Warranties</h3>
            <p>Equipment and installation warranty documents.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Health & Safety</h3>
            <p>Safety documentation and operational records.</p>
          </div>
        </div>
      </Section>

      {/* 5. Drawings */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Drawings & Schematics</span>
            <h2 className={styles.sectionHeading}>
              Keep drawings organised alongside the information they represent.
            </h2>
            <p className={styles.sectionCopy}>
              Drawings are often some of the most important — and most difficult
              to manage — information in a building. Pulse provides a structured
              way to organise architectural, mechanical, electrical and other
              technical drawings alongside the wider building record.
            </p>
            <p className={styles.sectionCopy}>
              Drawing categories and structures can be tailored to suit the
              building, including base build, post fit-out, XREF and
              discipline-specific records.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/drawings-schematics.webp")}
              alt="Pulse architectural, mechanical and electrical technical drawings"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 6. Base Build, Fit-Out and As-Built Information */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/documents-drawings-building-evolution.webp")}
              alt="Building information evolution stages"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Evolution</span>
            <h2 className={styles.sectionHeading}>
              Keep different stages of building information together.
            </h2>
            <p className={styles.sectionCopy}>
              Buildings often contain information from different stages of their
              development, from base-build and fit-out to post-fit-out drawings,
              as-built information and subsequent updates.
            </p>
            <p className={styles.sectionCopy}>
              This helps users understand what information they are looking at
              and where it belongs within the wider building record.
            </p>
          </div>
        </div>
      </Section>

      {/* 7. Drawing Relationships and XREFs */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Drawing Relationships</span>
            <h2 className={styles.sectionHeading}>
              Keep related drawings together.
            </h2>
            <p className={styles.sectionCopy}>
              Complex drawing sets can contain references to other drawings and
              files. Pulse can organise related drawing information so users
              have a clearer route through the drawing set.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/documents-drawings-relationships.webp")}
              alt="Primary drawing connected to XREFs and related drawings"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 8. Metadata Makes Documents Easier to Find */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/documents-drawings-metadata.webp")}
              alt="Document metadata demonstration"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Structured Metadata</span>
            <h2 className={styles.sectionHeading}>
              The document is only part of the information.
            </h2>
            <p className={styles.sectionCopy}>
              Useful metadata—such as document numbers, drawing numbers, titles,
              disciplines, sources, dates, revisions and file types—can make
              large collections of documents and drawings significantly easier
              to navigate.
            </p>
          </div>
        </div>
      </Section>

      {/* 9. Search */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Fast Search</span>
            <h2 className={styles.sectionHeading}>
              Find the document without knowing where it lives.
            </h2>
            <p className={styles.sectionCopy}>
              When a building contains thousands of documents, navigation alone
              isn't enough. Pulse search helps users find relevant documents,
              drawings, assets and other building information from a single
              search experience.
            </p>
            <div className={styles.actionLink}>
              <Link to="/search" className={styles.textLink}>
                Explore Search →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/fast-search.webp")}
              alt="Pulse search interface for documents and drawings"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 10. Document Preview */}
      {/* @todo: consider adding later */}
      {/* <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/placeholder.webp")}
              alt="Pulse document detail and preview interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Document Preview</span>
            <h2 className={styles.sectionHeading}>
              Get to the information before opening the file.
            </h2>
            <p className={styles.sectionCopy}>
              A document preview can give users a quick way to understand what a
              file contains before downloading or opening it, showing titles,
              file types, metadata and related assets.
            </p>
          </div>
        </div>
      </Section> */}

      {/* 11. Access From the Office or On Site */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/mobile/engineer-using-pulse.webp")}
              alt="Engineer accessing documents on site"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>On-Site Access</span>
            <h2 className={styles.sectionHeading}>
              The right document should be available where it's needed.
            </h2>
            <p className={styles.sectionCopy}>
              Technical information isn't only needed from a desktop. Building
              operators, engineers and facilities teams may need to access
              drawings and documentation while working on site using phones and
              tablets.
            </p>
          </div>
        </div>
      </Section>

      {/* 12. Connect Documents to Assets */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Asset Relationships</span>
            <h2 className={styles.sectionHeading}>
              Connect documents to the equipment they describe.
            </h2>
            <p className={styles.sectionCopy}>
              A document is often more useful when users can understand what it
              relates to. Pulse can connect building documents with the assets
              they describe, helping users move between an asset and its
              technical documentation.
            </p>
            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/asset-relationships.webp")}
              alt="Pulse asset and technical document relationships"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 13. AI */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information.webp")}
              alt="Pulse AI document queries"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Pulse AI</span>
            <h2 className={styles.sectionHeading}>
              The next step: ask questions about your documents.
            </h2>
            <p className={styles.sectionCopy}>
              Search can help users find the right document. AI can eventually
              help users understand the information contained within those
              documents, with answers grounded in relevant records.
            </p>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 14. Why Better Document Management Matters */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Make thousands of documents easier to use.
          </h2>
        </div>
        <div className={styles.benefitGrid}>
          <div className={styles.benefitCard}>
            <h3>Find information faster</h3>
            <p>
              Reduce time spent navigating folders and searching through
              documents.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Give documents context</h3>
            <p>
              Organise documents within the wider building information
              structure.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Improve accessibility</h3>
            <p>Make information available from the office or on site.</p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Preserve building knowledge</h3>
            <p>
              Keep important technical information in a consistent digital
              environment.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Reduce dependency on individuals</h3>
            <p>
              Make information easier to find without relying on someone knowing
              exactly where it is.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Create a foundation for search and AI</h3>
            <p>
              Well-structured documents provide a stronger foundation for
              advanced capabilities.
            </p>
          </div>
        </div>
      </Section>

      {/* 15. Product Showcase */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Product Showcase</span>
            <h2 className={styles.sectionHeading}>
              See documents and drawings in Pulse.
            </h2>
            <p className={styles.sectionCopy}>
              Experience how buildings, documents, drawings, metadata, related
              assets and search come together in a unified platform.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/documents-drawings/product-showcase.webp")}
              alt="Pulse documents and drawings platform showcase"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 16. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Make your building documents easier to find and use."
        copy="Whether you're managing an existing O&M record, thousands of technical drawings or the wider information behind a commercial building, Pulse provides a structured platform for bringing it together."
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(DocumentsDrawings)({
  meta: globalContent.seo.documentsDrawings,
});
